import React, { useEffect, useState, FunctionComponent, useCallback, useRef, useReducer } from 'react';
import * as signalR from '@microsoft/signalr';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { DynamicThemeProvider } from '@amtrav/themes';
import { Hub, Connection, Session, Message, Payload, User } from '@amtrav/chat-stream';
import { WebChatLauncher, OpenButton } from '@amtrav/web-chat';

import { useLocalStorageJsonHook, useSessionStorageJsonHook } from '@amtrav/external-storage-helpers';
import { useChatWidgetReducer } from './reducer';

const AID_KEY = 'WEBCHATAIDKEY';
const firstAid = Cookies.get(AID_KEY);

interface HubberProps {
  pin?: string;
  apiKey: string;
  url: string;
  botName?: string;
  theme?: string;
  hideOpener?: boolean;
  proactive?: boolean;
  forceSession?: boolean;
  isOurApp?: boolean;
}

interface ChatWidgetProps {
  botName?: string;
  hub: Hub;
  me: User;
  session: Session;
  onClose: () => void;
  onRestart: () => void;
}

const ChatWidget: FunctionComponent<ChatWidgetProps> = ({ botName, hub, me, session, onClose, onRestart }) => {
  const soundRef = useRef<HTMLAudioElement>();
  const smallSoundRef = useRef<HTMLAudioElement>();
  useEffect(() => {
    soundRef.current = new Audio('//cdn.amtrav.com/agent-console/whistle.mp3');
    smallSoundRef.current = new Audio('//cdn.amtrav.com/agent-console/new-chat-msg.mp3');
  }, []);

  const [state, { see, sendMessage, setTyping }] = useChatWidgetReducer(hub, session, me, () => {
    if (smallSoundRef.current) {
      smallSoundRef.current.play();
    }
  });

  return (
    <WebChatLauncher
      open
      session={state.session}
      messages={state.sessionMessages}
      onSend={sendMessage}
      onSeen={see}
      onType={setTyping}
      user={state.me}
      users={state.allUsers}
      botName={botName}
      onClose={onClose}
      onRestart={onRestart}
    />
  );
};

interface ChatWelcomeProps {
  botName?: string;
  me: User;
  onSend: (msg: string) => Promise<Message>;
  onClose: () => void;
  unavailable: boolean;
}

const ChatWelcome: FunctionComponent<ChatWelcomeProps> = ({ botName, me, unavailable, onSend, onClose }) => {
  return (
    <WebChatLauncher
      open
      session={null}
      messages={[]}
      unavailable={unavailable}
      onSend={onSend}
      onSeen={() => {}}
      onType={() => {}}
      user={me}
      users={{}}
      botName={botName}
      onClose={onClose}
    />
  );
};

interface ChatErrorProps {
  errorMessage: string;
  onClose: () => void;
}

const ChatError: FunctionComponent<ChatErrorProps> = ({ errorMessage, onClose }) => {
  return (
    <WebChatLauncher
      open
      errorMessage={errorMessage}
      session={null}
      messages={[]}
      unavailable
      onSend={() => {}}
      onSeen={() => {}}
      onType={() => {}}
      user={{}}
      users={{}}
      botName=""
      onClose={onClose}
    />
  );
};

interface HubberState {
  builder: () => Hub;
  connectionError?: string;
  initialMessage?: Payload;
  hub: Hub;
  me?: User;
  agentsAvailable: boolean;
  chatting: boolean;
  windowReady: boolean;
}

const initialHubberState = (builder: () => Hub): HubberState => ({
  builder,
  initialMessage: null,
  hub: null,
  chatting: false,
  agentsAvailable: false,
  windowReady: false,
});

interface InitHubberAction {
  type: 'initHubber';
  payload: Hub;
}

interface InitHubberWithMessageAction {
  type: 'initHubberWithMessage';
  payload: {
    hub: Hub;
    message: Payload;
  };
}

interface InitErrorAction {
  type: 'initError';
  payload: string;
}

interface SetInitialState {
  type: 'setInitialState';
  payload: {
    me: User;
    agentsAvailable: boolean;
  };
}

interface ClearHub {
  type: 'clearHub';
}

type HubberAction = InitHubberAction | InitHubberWithMessageAction | InitErrorAction | SetInitialState | ClearHub;

const hubberReducer = (state: HubberState, action: HubberAction) => {
  switch (action.type) {
    case 'initHubber': {
      return {
        ...state,
        hub: action.payload,
      };
    }
    case 'initHubberWithMessage': {
      return {
        ...state,
        hub: action.payload.hub,
        initialMessage: action.payload.message,
      };
    }
    case 'initError': {
      return {
        ...state,
        connectionError: action.payload,
      };
    }
    case 'setInitialState': {
      return {
        ...state,
        me: action.payload.me,
        agentsAvailable: action.payload.agentsAvailable,
        windowReady: true,
      };
    }
    case 'clearHub': {
      state.hub?.stop();
      return {
        ...state,
        hub: null,
        me: null,
        connectionError: null,
        initialMessage: null,
        chatting: false,
      };
    }
    default:
      console.warn('Unrecognized Action: ', action);
      return state;
  }
};

interface SessionerProps {
  hub: Hub;
  me: User;
  agentsAvailable: boolean;
  forceSession: boolean;
  initialMessage: Payload;
  keptSessionId: string;
  onSessionChange: (id: string) => void;
  botName: string;
  onClose: () => void;
}

const Sessioner = ({
  hub,
  initialMessage,
  me,
  forceSession,
  agentsAvailable,
  keptSessionId,
  onSessionChange = () => {},
  ...rest
}: SessionerProps) => {
  const [session, setSession] = useState(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (initialMessage || forceSession) {
      hub.getOrCreateSession().then((existingSession) => {
        if (initialMessage) {
          switch (initialMessage.type) {
            case 'text': {
              hub.sendTextMessage(existingSession.id, initialMessage.data.message);
              break;
            }
            case 'modify-trip': {
              hub.sendModifyTripMessage(existingSession.id, initialMessage.data.prompt, initialMessage.data.data);
              break;
            }
            default: {
              console.warn('Unsupported initial message type: ', initialMessage.type);
            }
          }
        }
        setSession(existingSession);
        onSessionChange(existingSession.id);
        setStarted(true);
      });
    } else {
      hub.getSession().then(async (existingSession) => {
        if (existingSession) {
          setSession(existingSession);
          onSessionChange(existingSession.id);
        } else if (keptSessionId) {
          await hub.fetchSessionById(keptSessionId).then((keptSession) => {
            setSession(keptSession);
            onSessionChange(keptSession.id);
          });
        }
        setStarted(true);
      });
    }
  }, [hub]);

  const onSend = async (msg: string) => {
    const newSession = await hub.getOrCreateSession();
    if (keptSessionId !== newSession.id) {
      onSessionChange(newSession.id);
    }
    setSession(newSession);
    return hub.sendTextMessage(newSession.id, msg);
  };
  return session ? (
    <ChatWidget me={me} session={session} hub={hub} {...rest} />
  ) : (
    started && <ChatWelcome unavailable={!agentsAvailable} me={me} onSend={onSend} {...rest} />
  );
};

interface KeptSessionStore {
  id: string;
}

const Hubber = ({ url, pin, apiKey, theme, hideOpener, proactive, botName, forceSession, isOurApp }: HubberProps) => {
  const aid = firstAid || uuidv4();
  Cookies.set(AID_KEY, aid);
  const unstartedRef = useRef<Hub>();
  const builder = useCallback(
    () =>
      new Hub(
        new signalR.HubConnectionBuilder()
          .withUrl(`${url}/chathub?ak=${apiKey}${pin ? `&pin=${pin}` : ''}${aid ? `&aid=${aid}` : ''}`)
          .configureLogging(signalR.LogLevel.Information)
          .withAutomaticReconnect()
          .build() as unknown as Connection,
      ),
    [],
  );
  const [state, dispatch] = useReducer(hubberReducer, initialHubberState(builder));
  const [keptSessionStore, setKeptSessionId, clearKeptSessionId] =
    useSessionStorageJsonHook<KeptSessionStore>('KeepOpenChat');
  const keptSessionId = keptSessionStore?.id;
  const ensureStarted = async () => {
    if (unstartedRef.current) return unstartedRef.current.started;
    unstartedRef.current = builder();
    return unstartedRef.current.started;
  };

  const handleConnectionError = (connErr) => {
    dispatch({
      type: 'initError',
      payload: 'We are having trouble connecting to the chat system. Please try reloading this page...',
    });
  };

  const closeChat = () => {
    clearKeptSessionId();
    dispatch({ type: 'clearHub' });
    unstartedRef.current = null;
  };

  const restartChat = () => {
    closeChat();
    window.startProactiveChat();
  };

  const connect = async () => {
    try {
      await ensureStarted();
    } catch (connErr) {
      handleConnectionError(connErr);
    }
    dispatch({ type: 'initHubber', payload: unstartedRef.current });
  };

  const connectModifyTrip = async (message, data) => {
    try {
      await ensureStarted();
    } catch (connErr) {
      handleConnectionError(connErr);
    }
    dispatch({
      type: 'initHubberWithMessage',
      payload: { hub: unstartedRef.current, message: { type: 'modify-trip', data: { prompt: message, data } } },
    });
  };

  const connectWithMessage = async (message) => {
    try {
      await ensureStarted();
    } catch (connErr) {
      handleConnectionError(connErr);
    }
    dispatch({
      type: 'initHubberWithMessage',
      payload: { hub: unstartedRef.current, message: { type: 'text', data: { message } } },
    });
  };

  if (proactive) {
    window.startProactiveChat = async () => {
      if (isOurApp) {
        if (window?.ReactNativeWebView?.postMessage) {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'startChat' }));
        }
      } else {
        connect();
      }
    };
  }
  window.startModifyChat = async (message: string, data: any) => {
    if (isOurApp) {
      if (window?.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'startModifyChat', payload: { prompt: message, data } }),
        );
      }
    } else {
      connectModifyTrip(message, data);
    }
  };

  window.startChatWithMessage = async (message: string) => {
    if (isOurApp) {
      if (window?.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'startChat', payload: { message } }));
      }
    } else {
      connectWithMessage(message);
    }
  };

  useEffect(() => {
    if (state.hub) {
      Promise.all([state.hub.fetchMe(), state.hub.isAnyAgentAvailable()]).then(([me, agentsAvailable]) => {
        dispatch({ type: 'setInitialState', payload: { me, agentsAvailable } });
      });
    }
  }, [state.hub]);

  const openButtonOrNothing = hideOpener ? null : <OpenButton onClick={connect} />;
  const hubReady = state.hub && state.me;

  useEffect(() => {
    if (keptSessionId) {
      connect();
    }
  }, []);
  return (
    <DynamicThemeProvider themeName={theme}>
      {state.connectionError ? (
        <ChatError errorMessage={state.connectionError} onClose={closeChat} />
      ) : hubReady ? (
        <Sessioner
          hub={state.hub}
          me={state.me}
          forceSession={forceSession}
          keptSessionId={keptSessionId}
          onSessionChange={(sessionId) => {
            setKeptSessionId({ id: sessionId });
          }}
          initialMessage={state.initialMessage}
          botName={botName}
          agentsAvailable={state.agentsAvailable}
          onClose={closeChat}
          onRestart={restartChat}
        />
      ) : (
        openButtonOrNothing
      )}
    </DynamicThemeProvider>
  );
};

export default Hubber;
